import { HttpParams } from '@angular/common/http';

import { ModelTransformer } from '../common/common.model';
import { User } from '../user/user.model';

export class AuthUser extends User {
    private _token: Token;
    amr: string[];
    private _permissions: EffectiveUserPermission[];

    get token(): Token {
        return this._token;
    }

    // eslint-disable-next-line accessor-pairs
    set permissions(value: EffectiveUserPermission[]) {
        this._permissions = value;
    }

    // eslint-disable-next-line @typescript-eslint/no-useless-constructor
    constructor() {
        super();
    }

    setAuth(token: Token): void {
        this._token = token;
        if (token) {
            try {
                this.amr = token.decodedAccessToken.payload.amr;
            } catch (err) {}
        }
    }

    isAuthenticated(): boolean {
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        return this._token && this._token.isValid();
    }

    hasSaml2AuthMethod(): boolean {
        // eslint-disable-next-line @typescript-eslint/prefer-includes
        return this.amr.indexOf('saml2') !== -1;
    }

    hasPermission(permission?: Permission, scope?: SecurityScope): boolean {
        return (
            this._permissions &&
            this._permissions.findIndex((eup) => {
                const p = permission == null || eup.permission === permission;
                const s = scope == null || eup.scope === scope;
                return p && s;
            }) !== -1
        );
    }
}

export enum AuthStatus {
    UNAUTHENTICATED,
    AUTHENTICATED,
    AUTHENTICATION_LOST,
}

export enum AuthState {
    UNSET,
    LOCAL_STORAGE,
    INTERACTIVE,
    NON_INTERACTIVE,
    LOGOUT,
    TIMEOUT,
    LOST,
}

export interface ExtToken {
    access_token: string;
    expires_in: number;
    token_type: string;
    refresh_token: string;
}

export interface ExtResetPasswordSecQuesAns {
    answer: string;
    userId: number;
    languageId?: number;
}

export interface ExtResetPasswordToken {
    tokenHash: string;
}

export interface ExtResetPassword {
    tokenHash: string;
    newPassword: string;
    languageId?: number;
}

export interface ExtVerifyResetPasswordToken {
    languageId: number;
}

export interface ExtPassword {
    oldPassword: string;
    newPassword: string;
}

export interface TokenRequest {
    username?: string;
    password?: string;
    code?: string;
    redirectUri?: string;
    refreshToken?: string;
    languageId?: number;
}

export class Token {
    private _access_token: string;
    private _expires_in: number;
    token_type: string;
    refresh_token: string;
    expires_at: Date;
    decodedAccessToken: any;

    get access_token(): string {
        return this._access_token;
    }

    set access_token(value: string) {
        this._access_token = value;
        this._decodeAccessToken();
    }

    get expires_in(): number {
        return this._expires_in;
    }

    set expires_in(value: number) {
        this._expires_in = value;
        this.expires_at = new Date(this.decodedAccessToken.payload.exp * 1000);
    }

    isValid(): boolean {
        return (
            this._access_token &&
            this._access_token.length !== 0 &&
            this.expires_at &&
            !isNaN(this.expires_at.getTime()) &&
            Date.now() < this.expires_at.getTime()
        );
    }

    private _decodeAccessToken(): void {
        try {
            const parts = this._access_token.split('.');
            this.decodedAccessToken = {
                header: JSON.parse(atob(parts[0])),
                payload: JSON.parse(atob(parts[1])),
            };
        } catch (err) {
            this.decodedAccessToken = null;
        }
    }

    toJSON(): string {
        return JSON.stringify({
            access_token: this._access_token,
            expires_in: this._expires_in,
            token_type: this.token_type,
            refresh_token: this.refresh_token,
        });
    }

    static fromJson(that: string): Token {
        const token = new Token();
        const _that = JSON.parse(that);
        token.access_token = _that.access_token || '';
        token.expires_in = parseInt(_that.expires_in) || 0;
        token.token_type = _that.token_type || '';
        token.refresh_token = _that.refresh_token || '';
        console.log(token);
        return token;
    }
}

export interface ResetPasswordSecQuesAns {
    answer: string;
    userId: number;
    languageId?: number;
}

export interface ResetPassword {
    tokenHash: string;
    password: string;
    passwordConfirm?: string;
    languageId?: number;
}

export interface Password {
    old: string;
    password: string;
    passwordConfirm?: string;
}

export enum Permission {
    VIEW = 101,
    EDIT = 102,
    DELETE = 103,
    ADD = 104,
    NAV_SUBMIT_EVALUATION = 319,
    NAV_VIEW_EVALUATIONS = 322,
    VIEW_UNPUBLISHED_DATA = 1001,
    PUBLISH_DATA = 1004,
    GENERATE_DATA = 1005,
    ADDITIONAL_TABS = 1006,
    TEAMS_CALL_CREATE_CALL = 1007,
    TEAMS_CALL_VIEW_TRANSCRIPT = 1008,
    EVALUATION_EDIT_PUBLISHED = 1012,
    EVALUATION_VIEW_QUESTION = 1017,
    EVALUATION_VIEW_QUESTIONS_RESPONSE = 1018,
    EVALUATION_VIEW_QUESTIONS_RESPONSE_REASON = 1019,
    EVALUATION_VIEW_MANAGER = 1034,
    EVALUATION_VIEW_EVALUATOR = 1035,
    EVALUATION_CONTACT_LENGTH = 1036,
    EVALUATION_QUEUE = 1037,
    TEAMS_CALL_EDIT_TRANSCRIPT = 1021,
    TEAMS_CALL_DELETE_TRANSCRIPT = 1022,
    COACHING_VIEW_TRANSCRIPT_SUMMARY_TAB = 1023,
    TEAMS_CALL_DELETE_TRANSCRIPT_QUOTES = 1024,
    TEAMS_CALL_CREATE_CHAT = 1032,
    TEAMS_CALL_IMPORT_TRANSCRIPT = 1200,
    TEAMS_CALL_VIEW_TASKS = 1201,
}

export enum SecurityScope {
    NAV = 1,
    USERS = 4,
    HOSTED_FILES = 23,
    COACHING = 24,
    COACHING_COMMITMENT = 26,
    SCORECARD = 50,
    TABLE_EDITOR = 51,
    EVALUATIONS = 57,
}

export interface ExtEffectiveUserPermission {
    permissionId: Permission;
    scopeId: SecurityScope;
}

export interface EffectiveUserPermission {
    permission: Permission;
    scope: SecurityScope;
}

export class LoginModelTransformer
    implements ModelTransformer<TokenRequest | Token, HttpParams | ExtToken>
{
    constructor(
        protected _clientId?: string,
        protected _grantType?:
            | 'password'
            | 'authorization_code'
            | 'refresh_token',
        protected _scopes?: string[],
    ) {}

    transform(model: ExtToken): Token {
        const transformed = new Token();
        transformed.access_token = model.access_token;
        transformed.expires_in = model.expires_in;
        transformed.token_type = model.token_type;
        transformed.refresh_token = model.refresh_token;
        return transformed;
    }

    reverseTransform(model: TokenRequest): HttpParams {
        let transformed = new HttpParams()
            .set('client_id', this._clientId)
            .set('grant_type', this._grantType);

        if (this._grantType === 'password') {
            transformed = transformed
                .set('username', model.username)
                .set('password', model.password)
                .set('scope', this._scopes.join(' '));
        }

        if (this._grantType === 'authorization_code') {
            transformed = transformed
                .set('code', model.code)
                .set('redirect_uri', model.redirectUri);
        }

        if (this._grantType === 'refresh_token') {
            transformed = transformed.set('refresh_token', model.refreshToken);
        }

        if (model.languageId != null && !isNaN(model.languageId)) {
            transformed = transformed.set(
                'language_id',
                model.languageId.toString(),
            );
        }
        return transformed;
    }
}

export class ResetPasswordSecQuesAnsModelTransformer
    implements
        ModelTransformer<ResetPasswordSecQuesAns, ExtResetPasswordSecQuesAns>
{
    reverseTransform(
        model: ResetPasswordSecQuesAns,
    ): ExtResetPasswordSecQuesAns {
        return {
            answer: model.answer,
            userId: model.userId,
            languageId: model.languageId,
        };
    }
}

export class ResetPasswordModelTransformer
    implements ModelTransformer<ResetPassword, ExtResetPassword>
{
    reverseTransform(model: ResetPassword): ExtResetPassword {
        return {
            tokenHash: model.tokenHash,
            newPassword: model.password,
            languageId: model.languageId,
        };
    }
}

export class PasswordModelTransformer
    implements ModelTransformer<Password, ExtPassword>
{
    reverseTransform(model: Password): ExtPassword {
        return {
            oldPassword: model.old,
            newPassword: model.password,
        };
    }
}

export class EffectiveUserPermissionModelSetTransformer
    implements
        ModelTransformer<
            EffectiveUserPermission[],
            ExtEffectiveUserPermission[]
        >
{
    transform(model: ExtEffectiveUserPermission[]): EffectiveUserPermission[] {
        return model.map((eup) => ({
            permission: eup.permissionId,
            scope: eup.scopeId,
        }));
    }
}
